/**
 * Styles for the widget, injected once as a scoped stylesheet.
 *
 * Why not Tailwind classes or CoreUI components: tenant-frontend is Tailwind
 * v4 + React 19, landlord-frontend is CoreUI/Bootstrap + React 18. Anything
 * borrowed from either would look wrong in the other, and importing
 * `@ui/shared` is what tenant-frontend explicitly avoids (its CoreUI
 * re-exports carry React types that disagree with React 19).
 *
 * Every rule is prefixed with `.lh-support` and every property is set
 * explicitly, so neither host's global resets nor its utility classes can
 * bleed in.
 */

export const STYLE_ELEMENT_ID = "lh-support-chat-styles";

/**
 * Lighthouse gold (#f2ca5b) is the brand colour and carries the launcher,
 * header and primary buttons.
 *
 * It takes DARK ink, never white: white on the gold measures 1.57:1, well
 * under the 4.5:1 WCAG AA minimum, while the navy ink is 8.96:1. Anywhere the
 * gold is a background, `onBrand` is the text colour.
 */
const COLORS = {
  brand: "#f2ca5b",
  brandHover: "#e5b93f",
  /** Text/icon colour for anything sitting on `brand`. */
  onBrand: "#0a2e46",
  primary: "#0a2e46",
  primaryHover: "#14425f",
  surface: "#ffffff",
  border: "#e5e7eb",
  text: "#1f2937",
  muted: "#6b7280",
  /** The tenant's own messages stay navy — gold bubbles against a gold
   *  header would flatten the transcript into one colour. */
  userBubble: "#0a2e46",
  botBubble: "#f3f4f6",
  danger: "#b91c1c",
  dangerBg: "#fef2f2",
  systemBg: "#fffbeb",
  systemText: "#92400e",
};

export const SUPPORT_CHAT_CSS = `
/* Typography reset, with NO positioning.
 *
 * Split out from .lh-support so a component can adopt the font and colour
 * baseline without being torn out of its container. .lh-support used to
 * carry both, which meant the phone-support card — which wants only the
 * reset — inherited position:fixed and escaped the card it sits in. */
.lh-support-base, .lh-support-base * { box-sizing: border-box; }
.lh-support-base {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.text};
}

/* The floating chat widget: the reset PLUS viewport pinning. Only the
 * launcher/panel should use this. */
.lh-support, .lh-support * { box-sizing: border-box; }
.lh-support {
  position: fixed;
  z-index: 2147483000;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.text};
}
.lh-support--bottom-right { right: 20px; bottom: 20px; }
.lh-support--bottom-left  { left: 20px;  bottom: 20px; }

.lh-support__launcher {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 18px; border: none; border-radius: 999px;
  background: ${COLORS.brand}; color: ${COLORS.onBrand};
  font-size: 14px; font-weight: 600; font-family: inherit;
  cursor: pointer; box-shadow: 0 6px 20px rgba(0,0,0,0.18);
  transition: background 120ms ease, transform 120ms ease;
}
.lh-support__icon { width: 18px; height: 18px; flex: 0 0 auto; display: block; }
.lh-support__launcher:hover { background: ${COLORS.brandHover}; transform: translateY(-1px); }
.lh-support__launcher:focus-visible { outline: 3px solid ${COLORS.primary}; outline-offset: 2px; }

.lh-support__panel {
  display: flex; flex-direction: column;
  width: 380px; height: 560px; max-height: calc(100vh - 48px);
  background: ${COLORS.surface}; border: 1px solid ${COLORS.border};
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 18px 48px rgba(0,0,0,0.22);
}

.lh-support__header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 14px 16px;
  background: ${COLORS.brand}; color: ${COLORS.onBrand}; flex: 0 0 auto;
}
.lh-support__title { font-weight: 600; font-size: 15px; margin: 0; }
.lh-support__close {
  background: transparent; border: none; color: ${COLORS.onBrand};
  font-size: 20px; line-height: 1; cursor: pointer;
  padding: 4px 6px; border-radius: 6px; font-family: inherit;
}
.lh-support__close:hover { background: rgba(10,46,70,0.12); }
.lh-support__close:focus-visible { outline: 2px solid ${COLORS.primary}; }

.lh-support__body {
  flex: 1 1 auto; overflow-y: auto; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
  background: ${COLORS.surface};
}

.lh-support__greeting { color: ${COLORS.muted}; font-size: 13px; margin: 0 0 4px; }

.lh-support__msg { max-width: 82%; padding: 9px 13px; border-radius: 14px; word-wrap: break-word; overflow-wrap: anywhere; }
.lh-support__msg--user { align-self: flex-end; background: ${COLORS.userBubble}; color: #fff; border-bottom-right-radius: 4px; }
.lh-support__msg--assistant,
.lh-support__msg--admin { align-self: flex-start; background: ${COLORS.botBubble}; color: ${COLORS.text}; border-bottom-left-radius: 4px; }
.lh-support__msg--system {
  white-space: pre-wrap;
  align-self: center; max-width: 100%; text-align: center;
  background: ${COLORS.systemBg}; color: ${COLORS.systemText};
  font-size: 12.5px; border-radius: 8px; padding: 7px 12px;
}
.lh-support__author { display: block; font-size: 11px; font-weight: 600; opacity: 0.75; margin-bottom: 2px; }

/* Markdown inside a bubble. Blocks carry their own spacing, so the first and
   last must not add padding inside the bubble. */
.lh-support__para { margin: 0 0 8px; }
.lh-support__para:last-child { margin-bottom: 0; }
.lh-support__msg strong { font-weight: 700; }
.lh-support__link { color: inherit; text-decoration: underline; font-weight: 600; }
.lh-support__link:hover { opacity: .8; }
/* On the navy user bubble the inherited colour is white, which is correct;
   on the grey assistant bubble use the brand navy so it reads as a link. */
.lh-support__msg--assistant .lh-support__link,
.lh-support__msg--admin .lh-support__link { color: ${COLORS.primary}; }
.lh-support__msg em { font-style: italic; }
.lh-support__list { margin: 0 0 8px; padding-left: 20px; }
.lh-support__list:last-child { margin-bottom: 0; }
.lh-support__list li { margin: 2px 0; }
.lh-support__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px; padding: 1px 4px; border-radius: 4px;
  background: rgba(0,0,0,0.06);
}
.lh-support__msg--user .lh-support__code { background: rgba(255,255,255,0.18); }

.lh-support__typing { align-self: flex-start; display: flex; gap: 4px; padding: 11px 14px; background: ${COLORS.botBubble}; border-radius: 14px; }
.lh-support__typing span { width: 6px; height: 6px; border-radius: 50%; background: ${COLORS.muted}; animation: lh-support-bounce 1.3s infinite ease-in-out; }
.lh-support__typing span:nth-child(2) { animation-delay: 0.15s; }
.lh-support__typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes lh-support-bounce { 0%,80%,100% { transform: translateY(0); opacity: .5; } 40% { transform: translateY(-4px); opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .lh-support__typing span { animation: none; }
  .lh-support__launcher { transition: none; }
}

.lh-support__error {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin: 0 16px 8px; padding: 8px 12px; border-radius: 8px;
  background: ${COLORS.dangerBg}; color: ${COLORS.danger}; font-size: 13px;
}
.lh-support__error button { background: none; border: none; color: inherit; cursor: pointer; font-size: 16px; line-height: 1; padding: 0 2px; font-family: inherit; }

.lh-support__footer { flex: 0 0 auto; border-top: 1px solid ${COLORS.border}; padding: 10px 12px; background: ${COLORS.surface}; }
/* Staged files, above the composer */
.lh-support__pending { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.lh-support__chip {
  display: inline-flex; align-items: center; gap: 6px;
  max-width: 100%; padding: 4px 8px; border-radius: 8px;
  background: ${COLORS.botBubble}; font-size: 12px; color: ${COLORS.text};
}
.lh-support__chip--error { background: ${COLORS.dangerBg}; color: ${COLORS.danger}; }
.lh-support__chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 160px; }
.lh-support__chip-status { opacity: .7; font-size: 11px; }
.lh-support__chip button {
  background: none; border: none; cursor: pointer; color: inherit;
  font-size: 14px; line-height: 1; padding: 0 2px; font-family: inherit;
}

/* Paperclip button */
.lh-support__attach {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; padding: 0;
  background: none; border: 1px solid ${COLORS.border}; border-radius: 10px;
  color: ${COLORS.muted}; cursor: pointer;
}
.lh-support__attach:hover:not(:disabled) { color: ${COLORS.text}; border-color: ${COLORS.muted}; }
.lh-support__attach:disabled { opacity: .5; cursor: not-allowed; }
.lh-support__attach:focus-visible { outline: 3px solid ${COLORS.primary}; outline-offset: 1px; }

/* Attachment links inside a bubble */
.lh-support__attachments { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.lh-support__attachment {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(0,0,0,0.06); border: none; border-radius: 8px;
  padding: 5px 9px; font-family: inherit; font-size: 13px; color: inherit;
  cursor: pointer; text-align: left; width: fit-content; max-width: 100%;
}
.lh-support__attachment:hover { background: rgba(0,0,0,0.12); }
.lh-support__attachment span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lh-support__msg--user .lh-support__attachment { background: rgba(255,255,255,0.18); }
.lh-support__msg--user .lh-support__attachment:hover { background: rgba(255,255,255,0.28); }
.lh-support__attachment .lh-support__icon { width: 14px; height: 14px; }

.lh-support__composer { display: flex; align-items: flex-end; gap: 8px; }
.lh-support__input {
  flex: 1 1 auto; resize: none; border: 1px solid ${COLORS.border};
  border-radius: 10px; padding: 9px 11px;
  font-family: inherit; font-size: 14px; line-height: 1.4; color: ${COLORS.text};
  background: #fff; max-height: 110px; min-height: 38px;
}
.lh-support__input:focus { outline: none; border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(242,202,91,0.35); }
.lh-support__input:disabled { background: #f9fafb; color: ${COLORS.muted}; }

.lh-support__send {
  flex: 0 0 auto; border: none; border-radius: 10px;
  background: ${COLORS.brand}; color: ${COLORS.onBrand};
  padding: 9px 15px; font-size: 14px; font-weight: 600; font-family: inherit; cursor: pointer;
}
.lh-support__send:disabled { background: #d1d5db; cursor: not-allowed; }
.lh-support__send:not(:disabled):hover { background: ${COLORS.brandHover}; }
.lh-support__send:focus-visible { outline: 3px solid ${COLORS.primary}; outline-offset: 1px; }


@media (max-width: 480px) {
  .lh-support--bottom-right, .lh-support--bottom-left { bottom: 12px; }
  .lh-support--bottom-right { right: 12px; }
  .lh-support--bottom-left  { left: 12px;  }

  /* Only the OPEN panel spans the viewport. Pinning both left and right on
     the closed launcher stretches its fixed container the full width of the
     screen — invisible, but it sits above the page and swallows taps meant
     for anything alongside it. */
  .lh-support--open.lh-support--bottom-right,
  .lh-support--open.lh-support--bottom-left { right: 12px; left: 12px; }
  .lh-support__panel { width: 100%; height: calc(100vh - 90px); }
}

/* --- Phone support code -------------------------------------------------
   Rendered inline on a page (not in the chat panel), so it inherits the
   host app's layout and only styles its own box. */
.lh-voice-code { font-size: 14px; line-height: 1.5; color: ${COLORS.text}; }
.lh-voice-code__intro { margin: 0 0 10px; color: ${COLORS.muted}; }
.lh-voice-code__btn {
  background: ${COLORS.brand}; color: ${COLORS.onBrand};
  border: none; border-radius: 8px; padding: 10px 16px;
  font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease;
}
.lh-voice-code__btn:hover:not(:disabled) { background: ${COLORS.brandHover}; }
.lh-voice-code__btn:disabled { opacity: 0.6; cursor: default; }
/* Monospace + wide tracking: this exists to be read aloud accurately. */
.lh-voice-code__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 32px; font-weight: 700; letter-spacing: 0.12em;
  color: ${COLORS.text}; margin: 4px 0 6px;
}
.lh-voice-code__meta { margin: 0 0 8px; font-size: 12px; color: ${COLORS.muted}; }
.lh-voice-code__link {
  background: none; border: none; padding: 0;
  color: ${COLORS.text}; font-size: 13px; text-decoration: underline;
  cursor: pointer;
}
.lh-voice-code__link:disabled { opacity: 0.6; cursor: default; }
.lh-voice-code__error { margin: 8px 0 0; font-size: 13px; color: ${COLORS.danger}; }

`;

/**
 * Inject the stylesheet once per document.
 *
 * Guarded by id so mounting the widget in both a layout and a page — or
 * remounting across route changes — can't duplicate it.
 */
export function ensureStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ELEMENT_ID;
  el.textContent = SUPPORT_CHAT_CSS;
  document.head.appendChild(el);
}
